import {
  ActionButton,
  ClearFiltersButton,
  ColumnPicker,
  ExportMenu,
  FilterCheckbox,
  FilterSelect,
  RefreshIcon,
  ReportToolbar,
} from '@/components/reports';
import type { ExportFormat, PdfOrientation } from '@/components/reports';
import { SkeletonReportsPage } from '@/components/ui/Skeleton';
import { useToast } from '@/hooks/useToast';
import { API_BASE_URL, api } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import type { Event } from '@eventflow/shared-types';
import { Ban, BarChart3, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface ReportFilters {
  eventId: string;
  paymentStatus: string;
  paymentMethod: string;
  onlyConfirmed: boolean;
}

interface CustomerReportRow {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerIdNumber: string;
  customerType: string;
  purchaseId: string;
  purchaseDate: string;
  eventName: string;
  eventDate: string;
  venue: string;
  seats: string;
  total: string;
  paymentStatus: string;
  paymentMethod: string;
}

type SortByOption = 'purchaseDate' | 'customerName' | 'total';
type SortDirection = 'asc' | 'desc';
type ReportTab = 'customers' | 'blocked-seats' | 'customer-stats';

interface CustomerTypeStats {
  customerType: string;
  totalPurchases: number;
  confirmedPurchases: number;
  pendingPurchases: number;
  totalAmount: number;
  confirmedAmount: number;
  uniqueCustomers: number;
}

interface GraduateProgramStats {
  graduateProgram: string;
  totalPurchases: number;
  confirmedPurchases: number;
  pendingPurchases: number;
  totalAmount: number;
  confirmedAmount: number;
  totalTickets: number;
}

interface BlockedSeatRow {
  seatIdentifier: string;
  tableId: string;
  eventName: string;
  eventDate: string;
  venue: string;
}

// ============================================================================
// CONSTANTES
// ============================================================================

const AVAILABLE_COLUMNS = [
  { key: 'customerName', label: 'Nombre Cliente' },
  { key: 'customerEmail', label: 'Email' },
  { key: 'customerPhone', label: 'Teléfono' },
  { key: 'customerIdNumber', label: 'INE / ID Oficial' },
  { key: 'customerType', label: 'Tipo Cliente' },
  { key: 'purchaseId', label: 'ID Compra' },
  { key: 'purchaseDate', label: 'Fecha Compra' },
  { key: 'eventName', label: 'Evento' },
  { key: 'eventDate', label: 'Fecha Evento' },
  { key: 'venue', label: 'Lugar' },
  { key: 'seats', label: 'Asientos' },
  { key: 'total', label: 'Total' },
  { key: 'paymentStatus', label: 'Estado Pago' },
  { key: 'paymentMethod', label: 'Método Pago' },
] as const;

const BLOCKED_SEATS_COLUMNS = [
  { key: 'seatIdentifier', label: 'Asiento' },
  { key: 'tableId', label: 'Mesa' },
  { key: 'eventName', label: 'Evento' },
  { key: 'eventDate', label: 'Fecha Evento' },
  { key: 'venue', label: 'Lugar' },
] as const;

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function ReportsPage() {
  // Tab activa
  const [activeTab, setActiveTab] = useState<ReportTab>('customers');

  // Estados generales
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [filters, setFilters] = useState<ReportFilters>({
    eventId: '',
    paymentStatus: '',
    paymentMethod: '',
    onlyConfirmed: false,
  });

  // Estados para reporte de clientes
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() =>
    AVAILABLE_COLUMNS.map((c) => c.key)
  );
  const [sortBy] = useState<SortByOption>('purchaseDate');
  const [sortDirection] = useState<SortDirection>('desc');
  const [previewData, setPreviewData] = useState<CustomerReportRow[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [customerPdfOrientation, setCustomerPdfOrientation] = useState<PdfOrientation>('landscape');
  const [recordCount, setRecordCount] = useState<number | null>(null);

  // Estados para asientos bloqueados
  const [selectedBlockedColumns, setSelectedBlockedColumns] = useState<string[]>(() =>
    BLOCKED_SEATS_COLUMNS.map((c) => c.key)
  );
  const [blockedSeatsPreview, setBlockedSeatsPreview] = useState<BlockedSeatRow[]>([]);
  const [isLoadingBlockedPreview, setIsLoadingBlockedPreview] = useState(false);
  const [blockedPdfOrientation, setBlockedPdfOrientation] = useState<PdfOrientation>('landscape');
  const [blockedRecordCount, setBlockedRecordCount] = useState<number | null>(null);

  // Estados para estadísticas por tipo de cliente
  const [customerTypeStats, setCustomerTypeStats] = useState<CustomerTypeStats[]>([]);
  const [graduateProgramStats, setGraduateProgramStats] = useState<GraduateProgramStats[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [statsEventName, setStatsEventName] = useState<string | null>(null);
  const [statsPdfOrientation, setStatsPdfOrientation] = useState<PdfOrientation>('landscape');

  const { success, error: showError } = useToast();

  // Calcular estadísticas de la vista previa
  const previewStats = useMemo(() => {
    if (previewData.length === 0) return null;
    const totalAmount = previewData.reduce((sum, row) => {
      const amount = Number.parseFloat(row.total?.replace('$', '').replace(',', '') || '0');
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
    const confirmed = previewData.filter((r) => r.paymentStatus === 'Confirmado').length;
    return { total: previewData.length, amount: totalAmount, confirmed };
  }, [previewData]);

  // Verificar si hay filtros activos
  const hasActiveFilters = Boolean(
    filters.eventId || filters.paymentStatus || filters.paymentMethod || filters.onlyConfirmed
  );

  // Cargar eventos del organizador
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsLoadingEvents(true);
        const data = await api.get<Event[]>('/events');
        setEvents(data);
      } catch (err) {
        console.error('Error fetching events:', err);
        showError('Error al cargar eventos');
      } finally {
        setIsLoadingEvents(false);
      }
    };

    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================================
  // FUNCIONES PARA REPORTE DE CLIENTES
  // ============================================================================

  const buildQueryString = () => {
    const params = new URLSearchParams();

    if (filters.eventId) {
      params.append('eventId', filters.eventId);
    }

    if (filters.onlyConfirmed) {
      params.append('onlyConfirmed', 'true');
    } else if (filters.paymentStatus) {
      params.append('paymentStatus', filters.paymentStatus);
    }

    if (filters.paymentMethod) {
      params.append('paymentMethod', filters.paymentMethod);
    }
    if (sortBy) {
      params.append('sortBy', sortBy);
      params.append('sortDirection', sortDirection);
    }
    if (selectedColumns.length > 0) {
      const columns = ['index', ...selectedColumns];
      params.append('columns', columns.join(','));
    }

    return params.toString();
  };

  const loadPreview = async () => {
    if (selectedColumns.length === 0) {
      showError('Selecciona al menos un campo para la vista previa');
      return;
    }
    try {
      setIsLoadingPreview(true);
      const queryString = buildQueryString();
      const data = await api.get<CustomerReportRow[]>(
        `/reports/customers?format=json${queryString ? `&${queryString}` : ''}`
      );
      const resultData = Array.isArray(data) ? data : [];
      setPreviewData(resultData);
      setRecordCount(resultData.length);
      if (resultData.length === 0) {
        success('No se encontraron datos con los filtros seleccionados');
      }
    } catch (err) {
      console.error('Error al cargar vista previa de reportes:', err);
      const message =
        err instanceof Error ? err.message : 'Error al cargar la vista previa de reportes';
      showError(message);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const downloadReport = async (format: ExportFormat) => {
    if (selectedColumns.length === 0) {
      showError('Selecciona al menos un campo antes de exportar');
      return;
    }
    try {
      setIsDownloading(format);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No estás autenticado. Por favor, inicia sesión nuevamente.');
      }

      const queryString = buildQueryString();
      const orientationParam = format === 'pdf' ? `&orientation=${customerPdfOrientation}` : '';
      const url = `${API_BASE_URL}/reports/customers?format=${format}${queryString ? `&${queryString}` : ''}${orientationParam}`;

      const blob = await new Promise<Blob>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.setRequestHeader('Authorization', `Bearer ${session.access_token}`);
        xhr.setRequestHeader(
          'Accept',
          format === 'pdf'
            ? 'application/pdf'
            : format === 'excel'
              ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              : 'text/csv'
        );

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else if (xhr.status === 0) {
            reject(
              new Error(
                'No se pudo conectar con el servidor. Verifica que el servidor esté corriendo.'
              )
            );
          } else {
            reject(new Error(`Error ${xhr.status}: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error('Error de conexión. Verifica que el servidor esté corriendo.'));
        };

        xhr.ontimeout = () => {
          reject(new Error('El servidor tardó demasiado en responder. Intenta con menos datos.'));
        };

        xhr.timeout = 60000;
        xhr.send();
      });

      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.style.display = 'none';

      const dateStr = new Date().toISOString().split('T')[0];
      const eventName = filters.eventId
        ? `-${
            events
              .find((e) => e.id === filters.eventId)
              ?.name.replace(/[^a-z0-9]/gi, '-')
              .toLowerCase() || 'evento'
          }`
        : '';
      const extension = format === 'excel' ? 'xlsx' : format;
      a.download = `reporte-clientes${eventName}-${dateStr}.${extension}`;

      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(urlObj);
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
      }, 100);

      success(`Reporte ${format.toUpperCase()} descargado exitosamente`);
    } catch (err) {
      console.error('Error downloading report:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Error al descargar el reporte. Intenta nuevamente.';
      showError(errorMessage);
    } finally {
      setIsDownloading(null);
    }
  };

  // ============================================================================
  // FUNCIONES PARA ASIENTOS BLOQUEADOS
  // ============================================================================

  const buildBlockedSeatsQueryString = () => {
    const params = new URLSearchParams();
    if (filters.eventId) {
      params.append('eventId', filters.eventId);
    }
    if (selectedBlockedColumns.length > 0) {
      const columns = ['index', ...selectedBlockedColumns];
      params.append('columns', columns.join(','));
    }
    return params.toString();
  };

  const loadBlockedSeatsPreview = async () => {
    if (selectedBlockedColumns.length === 0) {
      showError('Selecciona al menos un campo para la vista previa');
      return;
    }
    try {
      setIsLoadingBlockedPreview(true);
      const queryString = buildBlockedSeatsQueryString();
      const data = await api.get<BlockedSeatRow[]>(
        `/reports/blocked-seats?format=json${queryString ? `&${queryString}` : ''}`
      );
      const resultData = Array.isArray(data) ? data : [];
      setBlockedSeatsPreview(resultData);
      setBlockedRecordCount(resultData.length);
      if (resultData.length === 0) {
        success('No se encontraron asientos bloqueados');
      }
    } catch (err) {
      console.error('Error al cargar vista previa de asientos bloqueados:', err);
      const message = err instanceof Error ? err.message : 'Error al cargar la vista previa';
      showError(message);
    } finally {
      setIsLoadingBlockedPreview(false);
    }
  };

  const downloadBlockedSeatsReport = async (format: ExportFormat) => {
    if (selectedBlockedColumns.length === 0) {
      showError('Selecciona al menos un campo antes de exportar');
      return;
    }
    try {
      setIsDownloading(`blocked-${format}`);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No estás autenticado. Por favor, inicia sesión nuevamente.');
      }

      const queryString = buildBlockedSeatsQueryString();
      const orientationParam = format === 'pdf' ? `&orientation=${blockedPdfOrientation}` : '';
      const url = `${API_BASE_URL}/reports/blocked-seats?format=${format}${queryString ? `&${queryString}` : ''}${orientationParam}`;

      const blob = await new Promise<Blob>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.setRequestHeader('Authorization', `Bearer ${session.access_token}`);
        xhr.setRequestHeader(
          'Accept',
          format === 'excel'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : format === 'pdf'
              ? 'application/pdf'
              : 'text/csv'
        );

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error(`Error ${xhr.status}: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Error de red al descargar el reporte'));
        xhr.ontimeout = () => reject(new Error('Tiempo de espera agotado'));
        xhr.timeout = 30000;
        xhr.send();
      });

      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.style.display = 'none';

      const dateStr = new Date().toISOString().split('T')[0];
      const eventName = filters.eventId
        ? `-${
            events
              .find((e) => e.id === filters.eventId)
              ?.name.replace(/[^a-z0-9]/gi, '-')
              .toLowerCase() || 'evento'
          }`
        : '';
      const extension = format === 'excel' ? 'xlsx' : format === 'pdf' ? 'pdf' : 'csv';
      a.download = `asientos-bloqueados${eventName}-${dateStr}.${extension}`;

      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(urlObj);
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
      }, 100);

      success(`Reporte de asientos bloqueados ${format.toUpperCase()} descargado`);
    } catch (err) {
      console.error('Error downloading blocked seats report:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Error al descargar el reporte. Intenta nuevamente.';
      showError(errorMessage);
    } finally {
      setIsDownloading(null);
    }
  };

  // ============================================================================
  // FUNCIONES PARA ESTADÍSTICAS POR TIPO DE CLIENTE
  // ============================================================================

  const loadCustomerStats = async () => {
    try {
      setIsLoadingStats(true);
      const queryParams = filters.eventId ? `?eventId=${filters.eventId}` : '';
      const data = await api.get<{
        customerTypeStats: CustomerTypeStats[];
        graduateProgramStats: GraduateProgramStats[];
        eventName: string | null;
        totalPurchases: number;
      }>(`/reports/customer-stats${queryParams}`);

      setCustomerTypeStats(data.customerTypeStats || []);
      setGraduateProgramStats(data.graduateProgramStats || []);
      setStatsEventName(data.eventName);

      if (data.customerTypeStats.length === 0) {
        success('No se encontraron datos con los filtros seleccionados');
      }
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
      showError('Error al cargar las estadísticas');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const downloadCustomerStatsReport = async (format: ExportFormat) => {
    try {
      setIsDownloading(`stats-${format}`);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No estás autenticado. Por favor, inicia sesión nuevamente.');
      }

      const queryParams = new URLSearchParams();
      queryParams.append('format', format);
      if (filters.eventId) queryParams.append('eventId', filters.eventId);
      if (format === 'pdf') queryParams.append('orientation', statsPdfOrientation);

      const url = `${API_BASE_URL}/reports/customer-stats?${queryParams.toString()}`;

      const blob = await new Promise<Blob>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.setRequestHeader('Authorization', `Bearer ${session.access_token}`);
        xhr.setRequestHeader(
          'Accept',
          format === 'excel'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : format === 'pdf'
              ? 'application/pdf'
              : 'text/csv'
        );

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error(`Error ${xhr.status}: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Error de red al descargar el reporte'));
        xhr.ontimeout = () => reject(new Error('Tiempo de espera agotado'));
        xhr.timeout = 30000;
        xhr.send();
      });

      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.style.display = 'none';

      const dateStr = new Date().toISOString().split('T')[0];
      const eventName = filters.eventId
        ? `-${
            events
              .find((e) => e.id === filters.eventId)
              ?.name.replace(/[^a-z0-9]/gi, '-')
              .toLowerCase() || 'evento'
          }`
        : '';
      const extension = format === 'excel' ? 'xlsx' : format;
      a.download = `estadisticas-clientes${eventName}-${dateStr}.${extension}`;

      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(urlObj);
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
      }, 100);

      success(`Reporte de estadísticas ${format.toUpperCase()} descargado`);
    } catch (err) {
      console.error('Error downloading stats report:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Error al descargar el reporte. Intenta nuevamente.';
      showError(errorMessage);
    } finally {
      setIsDownloading(null);
    }
  };

  const clearFilters = () => {
    setFilters({
      eventId: '',
      paymentStatus: '',
      paymentMethod: '',
      onlyConfirmed: false,
    });
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (isLoadingEvents) {
    return <SkeletonReportsPage />;
  }

  // Renderizar contenido según la tab activa
  const renderCustomersTab = () => (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Toolbar horizontal */}
      <ReportToolbar
        leftContent={
          <>
            <FilterSelect
              id="eventFilter"
              label="Evento"
              value={filters.eventId}
              onChange={(value) => setFilters({ ...filters, eventId: value })}
              options={events.map((e) => ({ value: e.id, label: e.name }))}
              placeholder="Todos los eventos"
            />
            <FilterSelect
              id="statusFilter"
              label="Estado"
              value={filters.paymentStatus}
              onChange={(value) =>
                setFilters({ ...filters, paymentStatus: value, onlyConfirmed: false })
              }
              options={[
                { value: 'pending', label: 'Pendiente' },
                { value: 'confirmed', label: 'Confirmado' },
                { value: 'failed', label: 'Rechazado' },
              ]}
              placeholder="Pendientes+Confirmados"
            />
            <FilterCheckbox
              id="onlyConfirmed"
              label="Solo confirmadas"
              checked={filters.onlyConfirmed}
              onChange={(checked) =>
                setFilters({ ...filters, onlyConfirmed: checked, paymentStatus: '' })
              }
            />
            <ClearFiltersButton onClick={clearFilters} visible={hasActiveFilters} />
          </>
        }
        rightContent={
          <>
            <ColumnPicker
              columns={AVAILABLE_COLUMNS}
              selectedColumns={selectedColumns}
              onSelectionChange={setSelectedColumns}
              themeColor="blue"
            />
            <ActionButton
              onClick={loadPreview}
              loading={isLoadingPreview}
              variant="primary"
              icon={<RefreshIcon />}
            >
              Actualizar
            </ActionButton>
            <ExportMenu
              onExport={downloadReport}
              downloadingFormat={isDownloading}
              disabled={selectedColumns.length === 0}
              pdfOrientation={customerPdfOrientation}
              onOrientationChange={setCustomerPdfOrientation}
              themeColor="blue"
            />
          </>
        }
      />

      {/* Estadísticas rápidas */}
      {previewStats && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
            <div className="text-xs text-blue-600 font-medium">Registros</div>
            <div className="text-xl font-bold text-blue-700">{previewStats.total}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 border border-green-100">
            <div className="text-xs text-green-600 font-medium">Monto Total</div>
            <div className="text-xl font-bold text-green-700">
              ${previewStats.amount.toLocaleString('es-MX', { minimumFractionDigits: 0 })}
            </div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
            <div className="text-xs text-emerald-600 font-medium">Confirmados</div>
            <div className="text-xl font-bold text-emerald-700">{previewStats.confirmed}</div>
          </div>
        </div>
      )}

      {/* Tabla principal */}
      <div className="flex-1 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto h-full">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  #
                </th>
                {AVAILABLE_COLUMNS.filter((c) => selectedColumns.includes(c.key)).map((column) => (
                  <th
                    key={column.key}
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {previewData.length === 0 ? (
                <tr>
                  <td
                    colSpan={1 + selectedColumns.length}
                    className="px-3 py-12 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Users className="w-8 h-8 text-gray-300" />
                      <span>Presiona "Actualizar" para cargar datos</span>
                    </div>
                  </td>
                </tr>
              ) : (
                previewData.map((row, index) => (
                  <tr
                    key={`${row.purchaseId}-${index}`}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                  >
                    <td className="px-3 py-2 text-sm text-gray-500">{index + 1}</td>
                    {AVAILABLE_COLUMNS.filter((c) => selectedColumns.includes(c.key)).map(
                      (column) => (
                        <td
                          key={column.key}
                          className={`px-3 py-2 text-sm text-gray-700 ${
                            column.key === 'seats' ? 'max-w-xs truncate' : 'whitespace-nowrap'
                          }`}
                          title={String((row as any)[column.key] ?? '')}
                        >
                          {column.key === 'paymentStatus' ? (
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                row.paymentStatus === 'Confirmado'
                                  ? 'bg-green-100 text-green-700'
                                  : row.paymentStatus === 'Pendiente'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {row.paymentStatus}
                            </span>
                          ) : (
                            String((row as any)[column.key] ?? '')
                          )}
                        </td>
                      )
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderBlockedSeatsTab = () => (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Toolbar */}
      <ReportToolbar
        leftContent={
          <FilterSelect
            id="blockedEventFilter"
            label="Evento"
            value={filters.eventId}
            onChange={(value) => setFilters({ ...filters, eventId: value })}
            options={events.map((e) => ({ value: e.id, label: e.name }))}
            placeholder="Todos los eventos"
          />
        }
        rightContent={
          <>
            <ColumnPicker
              columns={BLOCKED_SEATS_COLUMNS}
              selectedColumns={selectedBlockedColumns}
              onSelectionChange={setSelectedBlockedColumns}
              themeColor="red"
            />
            <ActionButton
              onClick={loadBlockedSeatsPreview}
              loading={isLoadingBlockedPreview}
              variant="primary"
              icon={<RefreshIcon />}
            >
              Actualizar
            </ActionButton>
            <ExportMenu
              onExport={downloadBlockedSeatsReport}
              downloadingFormat={isDownloading?.startsWith('blocked-') ? isDownloading : null}
              disabled={selectedBlockedColumns.length === 0}
              pdfOrientation={blockedPdfOrientation}
              onOrientationChange={setBlockedPdfOrientation}
              themeColor="red"
            />
          </>
        }
      />

      {/* Tabla de asientos bloqueados */}
      <div className="flex-1 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto h-full">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  #
                </th>
                {BLOCKED_SEATS_COLUMNS.filter((c) => selectedBlockedColumns.includes(c.key)).map(
                  (column) => (
                    <th
                      key={column.key}
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap"
                    >
                      {column.label}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {blockedSeatsPreview.length === 0 ? (
                <tr>
                  <td
                    colSpan={1 + selectedBlockedColumns.length}
                    className="px-3 py-12 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Ban className="w-8 h-8 text-gray-300" />
                      <span>Presiona "Actualizar" para cargar datos</span>
                    </div>
                  </td>
                </tr>
              ) : (
                blockedSeatsPreview.map((row, index) => (
                  <tr
                    key={`${row.seatIdentifier}-${index}`}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                  >
                    <td className="px-3 py-2 text-sm text-gray-500">{index + 1}</td>
                    {BLOCKED_SEATS_COLUMNS.filter((c) =>
                      selectedBlockedColumns.includes(c.key)
                    ).map((column) => (
                      <td
                        key={column.key}
                        className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap"
                      >
                        {String((row as any)[column.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {blockedRecordCount !== null && blockedRecordCount > 0 && (
        <div className="mt-3 text-center text-xs text-gray-500">
          {blockedRecordCount} asiento(s) bloqueado(s) encontrado(s)
        </div>
      )}
    </div>
  );

  const renderStatsTab = () => (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Toolbar */}
      <ReportToolbar
        leftContent={
          <FilterSelect
            id="statsEventFilter"
            label="Evento"
            value={filters.eventId}
            onChange={(value) => setFilters({ ...filters, eventId: value })}
            options={events.map((e) => ({ value: e.id, label: e.name }))}
            placeholder="Todos los eventos"
          />
        }
        rightContent={
          <>
            <ActionButton
              onClick={loadCustomerStats}
              loading={isLoadingStats}
              variant="primary"
              icon={<RefreshIcon />}
            >
              Cargar
            </ActionButton>
            <ExportMenu
              onExport={downloadCustomerStatsReport}
              downloadingFormat={isDownloading?.startsWith('stats-') ? isDownloading : null}
              disabled={customerTypeStats.length === 0}
              pdfOrientation={statsPdfOrientation}
              onOrientationChange={setStatsPdfOrientation}
              themeColor="emerald"
            />
          </>
        }
      />

      {statsEventName && (
        <div className="mb-4 text-sm text-emerald-600 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Mostrando estadísticas para: <strong>{statsEventName}</strong>
        </div>
      )}

      {/* Contenido de estadísticas */}
      <div className="flex-1 overflow-y-auto space-y-6">
        {/* Resumen por tipo de cliente */}
        {customerTypeStats.length > 0 ? (
          <>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Resumen por Tipo de Cliente
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {customerTypeStats.map((stat) => (
                  <div
                    key={stat.customerType}
                    className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-gray-900">{stat.customerType}</span>
                      <span className="text-2xl font-bold text-emerald-600">
                        {stat.totalPurchases}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-green-50 rounded p-2">
                        <div className="text-green-600">Confirmadas</div>
                        <div className="font-bold text-green-700">{stat.confirmedPurchases}</div>
                      </div>
                      <div className="bg-yellow-50 rounded p-2">
                        <div className="text-yellow-600">Pendientes</div>
                        <div className="font-bold text-yellow-700">{stat.pendingPurchases}</div>
                      </div>
                      <div className="bg-blue-50 rounded p-2">
                        <div className="text-blue-600">Monto Total</div>
                        <div className="font-bold text-blue-700">
                          ${stat.totalAmount.toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                        </div>
                      </div>
                      <div className="bg-purple-50 rounded p-2">
                        <div className="text-purple-600">Clientes</div>
                        <div className="font-bold text-purple-700">{stat.uniqueCustomers}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Desglose por programa */}
            {graduateProgramStats.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Desglose por Programa Educativo
                </h3>
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto max-h-80">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-emerald-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">#</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">
                            Programa
                          </th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">
                            Compras
                          </th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">
                            Conf.
                          </th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">
                            Boletos
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700">
                            Monto
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {graduateProgramStats.map((stat, index) => (
                          <tr
                            key={stat.graduateProgram}
                            className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          >
                            <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                            <td
                              className="px-3 py-2 text-gray-900 max-w-xs truncate"
                              title={stat.graduateProgram}
                            >
                              {stat.graduateProgram}
                            </td>
                            <td className="px-3 py-2 text-center font-medium text-gray-900">
                              {stat.totalPurchases}
                            </td>
                            <td className="px-3 py-2 text-center text-green-600">
                              {stat.confirmedPurchases}
                            </td>
                            <td className="px-3 py-2 text-center text-blue-600">
                              {stat.totalTickets}
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900">
                              $
                              {stat.totalAmount.toLocaleString('es-MX', {
                                minimumFractionDigits: 0,
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Presiona "Cargar" para ver las estadísticas</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col max-w-[1600px] mx-auto px-3 sm:px-4 py-4">
      {/* Header compacto */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-md">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900">Centro de Reportes</h1>
            <p className="text-xs text-gray-500 hidden sm:block">
              Genera y exporta reportes de tus eventos
            </p>
          </div>
        </div>
      </div>

      {/* Tabs de navegación */}
      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('customers')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'customers'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Users className="w-4 h-4" />
          <span className="hidden sm:inline">Clientes</span>
          {recordCount !== null && (
            <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
              {recordCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('blocked-seats')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'blocked-seats'
              ? 'bg-white text-red-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Ban className="w-4 h-4" />
          <span className="hidden sm:inline">Bloqueados</span>
          {blockedRecordCount !== null && (
            <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
              {blockedRecordCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('customer-stats')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'customer-stats'
              ? 'bg-white text-emerald-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          <span className="hidden sm:inline">Estadísticas</span>
        </button>
      </div>

      {/* Contenido de la tab activa */}
      {activeTab === 'customers' && renderCustomersTab()}
      {activeTab === 'blocked-seats' && renderBlockedSeatsTab()}
      {activeTab === 'customer-stats' && renderStatsTab()}
    </div>
  );
}
